import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface ColorPickerProps {
  color: string;
  onColorChange: (color: string) => void;
  showWheel?: boolean;
  showSwatches?: boolean;
  swatches?: Array<{ name: string; value: string }>;
  label?: string;
}

const ColorPicker: React.FC<ColorPickerProps> = ({
  color,
  onColorChange,
  showWheel = true,
  showSwatches = true,
  swatches = [],
  label
}) => {
  const { t } = useTranslation();
  const [isWheelOpen, setIsWheelOpen] = useState(false);
  const [hue, setHue] = useState(0);
  const [saturation, setSaturation] = useState(100);
  const [lightness, setLightness] = useState(50);
  const colorInputRef = useRef<HTMLInputElement>(null);

  // 색상을 HSL로 변환하는 함수
  const hexToHSL = (hex: string) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  };

  // HSL을 HEX로 변환하는 함수
  const hslToHex = (h: number, s: number, l: number) => {
    h /= 360;
    s /= 100;
    l /= 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs((h * 6) % 2 - 1));
    const m = l - c / 2;
    let r = 0, g = 0, b = 0;

    if (0 <= h && h < 1/6) {
      r = c; g = x; b = 0;
    } else if (1/6 <= h && h < 1/3) {
      r = x; g = c; b = 0;
    } else if (1/3 <= h && h < 1/2) {
      r = 0; g = c; b = x;
    } else if (1/2 <= h && h < 2/3) {
      r = 0; g = x; b = c;
    } else if (2/3 <= h && h < 5/6) {
      r = x; g = 0; b = c;
    } else if (5/6 <= h && h <= 1) {
      r = c; g = 0; b = x;
    }

    const rHex = Math.round((r + m) * 255).toString(16).padStart(2, '0');
    const gHex = Math.round((g + m) * 255).toString(16).padStart(2, '0');
    const bHex = Math.round((b + m) * 255).toString(16).padStart(2, '0');

    return `#${rHex}${gHex}${bHex}`;
  };

  // 초기 색상 설정 (외부에서 색상이 변경될 때만)
  useEffect(() => {
    const hsl = hexToHSL(color);
    setHue(hsl.h);
    setSaturation(hsl.s);
    setLightness(hsl.l);
  }, [color]);

  // HSL 슬라이더 변경 핸들러
  const handleHueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newHue = parseInt(e.target.value);
    setHue(newHue);
    const newColor = hslToHex(newHue, saturation, lightness);
    onColorChange(newColor);
  };

  const handleSaturationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSaturation = parseInt(e.target.value);
    setSaturation(newSaturation);
    const newColor = hslToHex(hue, newSaturation, lightness);
    onColorChange(newColor);
  };

  const handleLightnessChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newLightness = parseInt(e.target.value);
    setLightness(newLightness);
    const newColor = hslToHex(hue, saturation, newLightness);
    onColorChange(newColor);
  };

  const handleColorInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onColorChange(e.target.value);
  };

  const handleSwatchClick = (swatchColor: string) => {
    onColorChange(swatchColor);
  };

  const normalizedColor = (color || '').toLowerCase();
  const isCustomColor = !swatches.some(s => s.value.toLowerCase() === normalizedColor);

  // 밝은 색 위에서는 어두운 체크 아이콘을 사용해 대비 확보
  const isLightColor = (hex: string) => {
    try {
      return hexToHSL(hex).l > 72;
    } catch {
      return false;
    }
  };

  return (
    <div className="color-picker-container rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
      {/* 헤더: 라벨 + 현재 색상 칩 */}
      <div className="mb-4 flex items-center justify-between gap-3">
        {label && (
          <label className="text-sm font-semibold text-gray-800">
            {label}
          </label>
        )}
        <span className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1">
          <span
            className="h-4 w-4 rounded-full shadow-inner ring-1 ring-inset ring-black/10"
            style={{ backgroundColor: color }}
            aria-hidden="true"
          />
          <span className="font-mono text-xs font-medium uppercase tracking-wide text-gray-600">
            {color}
          </span>
        </span>
      </div>

      {/* 색상 스와치 */}
      {showSwatches && swatches.length > 0 && (
        <div className="color-picker-swatches flex flex-wrap items-center gap-2.5">
          {swatches.map((swatch) => {
            const isSelected = swatch.value.toLowerCase() === normalizedColor;
            return (
              <button
                key={swatch.value}
                type="button"
                onClick={() => handleSwatchClick(swatch.value)}
                aria-pressed={isSelected}
                className={`relative flex h-9 w-9 items-center justify-center rounded-full shadow-sm transition-all duration-150 ${
                  isSelected
                    ? 'ring-2 ring-blue-600 ring-offset-2'
                    : 'ring-1 ring-inset ring-black/10 hover:scale-110 hover:shadow'
                }`}
                style={{ backgroundColor: swatch.value }}
                title={swatch.name}
              >
                {isSelected && (
                  <svg
                    className={`h-4 w-4 ${isLightColor(swatch.value) ? 'text-gray-700' : 'text-white drop-shadow'}`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={3}
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            );
          })}

          {/* 커스텀 색상 (네이티브 컬러 픽커) */}
          <label
            className={`relative flex h-9 w-9 cursor-pointer items-center justify-center rounded-full shadow-sm transition-all duration-150 ${
              isCustomColor
                ? 'ring-2 ring-blue-600 ring-offset-2'
                : 'ring-1 ring-inset ring-black/10 hover:scale-110 hover:shadow'
            }`}
            style={{
              background: 'conic-gradient(from 180deg, #ef4444, #f59e0b, #84cc16, #06b6d4, #3b82f6, #a855f7, #ef4444)'
            }}
            title={color}
          >
            <span
              className="pointer-events-none absolute inset-[3px] rounded-full ring-1 ring-inset ring-black/5"
              style={{ backgroundColor: isCustomColor ? color : '#ffffff' }}
              aria-hidden="true"
            />
            {isCustomColor && (
              <svg
                className={`pointer-events-none relative h-4 w-4 ${isLightColor(color) ? 'text-gray-700' : 'text-white drop-shadow'}`}
                fill="none"
                stroke="currentColor"
                strokeWidth={3}
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            )}
            <input
              ref={colorInputRef}
              type="color"
              value={color}
              onChange={handleColorInputChange}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            />
          </label>
        </div>
      )}

      {/* 커스텀 휠 컬러 픽커 */}
      {showWheel && (
        <div className="mt-4 border-t border-gray-100 pt-3">
          <button
            type="button"
            onClick={() => setIsWheelOpen(!isWheelOpen)}
            aria-expanded={isWheelOpen}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 transition-colors hover:text-blue-700"
          >
            {isWheelOpen
              ? t('common.close', 'Close')
              : t('cardColorSelection', {
                  defaultValue: t('landing.cardColorSelection', 'Color Selection')
                })}
            <svg
              className={`h-4 w-4 transition-transform duration-150 ${isWheelOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {isWheelOpen && (
            <div className="mt-3 space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
              {/* 색조 슬라이더 */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-600">
                    {t('colorPicker.hue', {
                      defaultValue: t('landing.colorPicker.hue', 'Hue')
                    })}
                  </label>
                  <span className="font-mono text-xs text-gray-500">{hue}°</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="360"
                  value={hue}
                  onChange={handleHueChange}
                  className="color-picker-slider h-2.5 w-full cursor-pointer appearance-none rounded-full"
                  style={{
                    background: `linear-gradient(to right,
                      hsl(0, 100%, 50%),
                      hsl(60, 100%, 50%),
                      hsl(120, 100%, 50%),
                      hsl(180, 100%, 50%),
                      hsl(240, 100%, 50%),
                      hsl(300, 100%, 50%),
                      hsl(360, 100%, 50%)
                    )`
                  }}
                />
              </div>

              {/* 채도 슬라이더 */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-600">
                    {t('colorPicker.saturation', {
                      defaultValue: t('landing.colorPicker.saturation', 'Saturation')
                    })}
                  </label>
                  <span className="font-mono text-xs text-gray-500">{saturation}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={saturation}
                  onChange={handleSaturationChange}
                  className="color-picker-slider h-2.5 w-full cursor-pointer appearance-none rounded-full"
                  style={{
                    background: `linear-gradient(to right,
                      hsl(${hue}, 0%, ${lightness}%),
                      hsl(${hue}, 100%, ${lightness}%)
                    )`
                  }}
                />
              </div>

              {/* 명도 슬라이더 */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-600">
                    {t('colorPicker.lightness', {
                      defaultValue: t('landing.colorPicker.lightness', 'Lightness')
                    })}
                  </label>
                  <span className="font-mono text-xs text-gray-500">{lightness}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={lightness}
                  onChange={handleLightnessChange}
                  className="color-picker-slider h-2.5 w-full cursor-pointer appearance-none rounded-full"
                  style={{
                    background: `linear-gradient(to right,
                      hsl(${hue}, ${saturation}%, 0%),
                      hsl(${hue}, ${saturation}%, 50%),
                      hsl(${hue}, ${saturation}%, 100%)
                    )`
                  }}
                />
              </div>

              {/* 현재 색상 미리보기 */}
              <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3">
                <div
                  className="h-10 w-10 rounded-lg shadow-inner ring-1 ring-inset ring-black/10"
                  style={{ backgroundColor: color }}
                />
                <div className="text-xs leading-5">
                  <div className="font-mono font-semibold uppercase tracking-wide text-gray-800">{color}</div>
                  <div className="font-mono text-gray-400">
                    HSL({hue}, {saturation}%, {lightness}%)
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ColorPicker;
